import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { useUpdateUserMutation } from "../api/userApi";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { setSession } from "../slices/sessionSlice";
import { baseApi } from "../api/api";

export const useLanguageSync = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.session.user);
  const idToken = useSelector((state: RootState) => state.session.idToken);
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  // Set i18n language when user data loads
  useEffect(() => {
    if (currentUser?.preferred_language) {
      const userLanguage = currentUser.preferred_language;

      // Only change if different from current i18n language
      if (i18n.language !== userLanguage) {
        i18n.changeLanguage(userLanguage);
      }
    }
  }, [currentUser?.preferred_language, i18n]);

  // Function to change language and update user
  const changeLanguage = async (languageCode: string) => {
    const previousLanguage = currentUser?.preferred_language;
    try {
      // Update i18n immediately for responsive UI
      await i18n.changeLanguage(languageCode);

      // Update Redux session immediately so X-Language header is correct for re-fetches
      if (currentUser && idToken) {
        dispatch(setSession({
          user: { ...currentUser, preferred_language: languageCode },
          idToken,
        }));
      }

      // Update user in database (use uid route — always available from session)
      if (currentUser?.uid) {
        await updateUser({
          id: `uid/${currentUser.uid}`,
          data: { preferred_language: languageCode },
        }).unwrap();
      }

      // Reset API cache so all mounted queries refetch with the new language
      dispatch(baseApi.util.resetApiState());
    } catch (error) {
      // RTK Query rejections are plain objects of shape FetchBaseQueryError ({status, data, error?})
      // — wrap them in a real Error so Sentry surfaces a useful title + extra context.
      const err = error as { status?: number | string; data?: unknown; error?: string };
      const status = err?.status ?? "unknown";
      const detail =
        (typeof err?.error === "string" ? err.error : undefined) ??
        (typeof err?.data === "object" && err?.data !== null && "error" in err.data
          ? String((err.data as { error?: unknown }).error)
          : undefined) ??
        "no detail";
      Sentry.captureException(
        new Error(`changeLanguage failed: status=${status} detail=${detail}`),
        {
          extra: { rtkError: error, languageCode, previousLanguage, uid: currentUser?.uid },
          tags: { feature: "changeLanguage", status: String(status) },
        }
      );
      // Revert i18n and Redux on error
      if (previousLanguage) {
        i18n.changeLanguage(previousLanguage);
        if (currentUser && idToken) {
          dispatch(setSession({
            user: { ...currentUser, preferred_language: previousLanguage },
            idToken,
          }));
        }
      }
    }
  };

  return {
    currentLanguage: i18n.language,
    changeLanguage,
    isUpdating,
  };
};
